'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import type { ReferenceCategory } from '@eliadmin/core/src/api/references/references-types';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { ReferenceCard } from './reference-card';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { ReferencesSkeleton } from './references-skeleton';

const ALL_TAB_VALUE = 'all';

export function ReferencesList({
  categories,
  locale,
}: {
  categories: ReferenceCategory[];
  locale: string;
}) {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_TAB_VALUE);
  const activeTabId = activeTab === ALL_TAB_VALUE ? undefined : Number(activeTab);
  const activeTabLabel =
    activeTab === ALL_TAB_VALUE
      ? t('text1947')
      : (categories.find(category => category.id === activeTabId)?.name ?? '');

  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, activeTab, locale],
    queryFn: ({ pageParam = 1 }) =>
      fetchReferences({
        locale,
        query: {
          page: Number(pageParam),
          results: 6,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      }),
    getNextPageParam: lastPage => {
      if (lastPage.meta.current_page < lastPage.meta.last_page) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const references = referencesQuery.data?.pages.flatMap(page => page.data) ?? [];

  const isLoading = referencesQuery.isLoading;

  return (
    <section className={cn('custom-section', 'pr-0 pt-6')}>
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn('custom-container', 'flex flex-col items-start justify-start gap-12 pb-16')}
      >
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full items-center justify-start gap-2 overflow-x-auto rounded-none bg-transparent p-0 sm:gap-2',
          )}
        >
          <TabsTrigger
            value={ALL_TAB_VALUE}
            className={cn(
              'ea-universal-tabs__tab-trigger',
              'flex h-[45px] items-center justify-center whitespace-nowrap rounded-[8px] px-4 text-sm transition-all sm:px-5 sm:text-base lg:px-6',
            )}
          >
            {t('text1947')}
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              value={String(category.id)}
              key={category.id}
              className={cn(
                'ea-universal-tabs__tab-trigger',
                'flex h-[45px] items-center justify-center gap-2 whitespace-nowrap rounded-[8px] pl-3 pr-4 text-sm transition-all sm:pl-4 sm:pr-5 sm:text-base lg:pl-[22px] lg:pr-6',
              )}
            >
              <Image
                src={category?.image?.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(category?.image, category?.name)}
                width={0}
                height={0}
                className="max-h-5 min-h-5 min-w-5 max-w-5 object-contain object-center"
              />
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={activeTab} className="w-full pr-6">
          {isLoading && <ReferencesSkeleton />}
          {referencesQuery.error && <p className="pt-8">{t('text2364')}</p>}
          {!referencesQuery.isLoading && references.length === 0 && (
            <Paragraph size="lg" color="muted-foreground" className="w-full pb-16 pt-8 text-center">
              Pre kategóriu <span className="font-bold text-black">{activeTabLabel}</span> sme
              nenašli žiadne referencie.
            </Paragraph>
          )}
          {!referencesQuery.isLoading && references.length > 0 && (
            <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
              {references.map(reference => (
                <ReferenceCard key={reference.id} reference={reference} />
              ))}
            </div>
          )}
        </TabsContent>
        <div
          className={cn('flex w-full items-center justify-center pr-5 sm:pr-0', {
            hidden: !referencesQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => referencesQuery.fetchNextPage()}
            isLoading={referencesQuery.isFetchingNextPage}
            className="w-full rounded-full sm:w-fit"
          >
            {t('text338')}
          </Button>
        </div>
      </Tabs>
    </section>
  );
}
